// src/pages/Besoins.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { besoinsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Search, RefreshCw, Building2, MapPin, User,
  X, ChevronRight, Layers,Filter
} from 'lucide-react';

// ── Mapping états CSV/AO → couleurs kanban ──────────────────────
const ETAT_CONFIG = {
  'Nouveau':                                           { color: '#38BDF8', bg: 'rgba(56,189,248,0.12)',   label: 'Nouveau' },
  'En cour de traitement':                             { color: '#FF4100', bg: 'rgba(255,65,0,0.12)',     label: 'En traitement' },
  'En avance de phase':                                { color: '#A78BFA', bg: 'rgba(167,139,250,0.12)', label: 'Avance de phase' },
  'En avance de phase via apporteur ou contact chaud': { color: '#A78BFA', bg: 'rgba(167,139,250,0.12)', label: 'Avance phase' },
  'Gagné':                                             { color: '#22C55E', bg: 'rgba(34,197,94,0.12)',   label: 'Gagné' },
  'Perdu':                                             { color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   label: 'Perdu' },
  'Abandonné':                                         { color: '#F97316', bg: 'rgba(249,115,22,0.12)', label: 'Abandonné' },
  'reporté':                                           { color: '#FCD34D', bg: 'rgba(252,211,77,0.12)', label: 'Reporté' },
  'cloturé':                                           { color: '#6B7280', bg: 'rgba(107,114,128,0.12)', label: 'Clôturé' },
  'rejeté':                                            { color: '#DC2626', bg: 'rgba(220,38,38,0.12)',  label: 'Rejeté' },
};
const ALL_ETATS = Object.keys(ETAT_CONFIG);

function EtatBadge({ etat }) {
  const cfg = ETAT_CONFIG[etat] || { color: 'var(--text-muted)', bg: 'var(--bg-elevated)', label: etat || '—' };
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:4,
      padding:'3px 10px', borderRadius:999, fontSize:'0.68rem', fontWeight:700,
      background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.color}33`, whiteSpace:'nowrap',
    }}>{cfg.label}</span>
  );
}

function SourceBadge({ source }) {
  if (source === 'AO') return (
    <span style={{ padding:'2px 8px', borderRadius:6, fontSize:'0.65rem', fontWeight:700, background:'rgba(255,65,0,0.12)', color:'#FF6A38', border:'1px solid rgba(255,65,0,0.22)' }}>AO traité</span>
  );
  return (
    <span style={{ padding:'2px 8px', borderRadius:6, fontSize:'0.65rem', fontWeight:700, background:'rgba(56,189,248,0.10)', color:'#38BDF8', border:'1px solid rgba(56,189,248,0.20)' }}>CRM</span>
  );
}

// ── Page principale ─────────────────────────────────────────────
export default function Besoins() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = (user?.role || '').toUpperCase();
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
 const [search, setSearch] = useState('');

const [filters, setFilters] = useState({
  etat: '',
  source: '',
  responsable: ''
});

const [showFilters, setShowFilters] = useState(false);
  const [stats,       setStats]       = useState(null);
const hasFilters = Object.values(filters).some(Boolean);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (search)      params.search      = search;
     if (filters.etat)
  params.etat = filters.etat;

if (filters.source)
  params.source = filters.source;

if (filters.responsable)
  params.responsable = filters.responsable;
      const [res, statsRes] = await Promise.allSettled([
        besoinsAPI.list(params),
        besoinsAPI.stats(),
      ]);
      setItems(res.status === 'fulfilled' ? (res.value.data || []) : []);
      setStats(statsRes.status === 'fulfilled' ? statsRes.value.data : null);
    } catch {
      toast.error('Erreur chargement des besoins');
    } finally { setLoading(false); }
  }, [search, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    try {
      const res = await besoinsAPI.importCsv();
      toast.success(`Synchronisation terminée — ${res.data.created} nouveaux, ${res.data.updated} mis à jour`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur synchronisation');
    }
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>Besoins clients</h1>
          <p>Appels d'offres et missions — CRM + pipeline commercial</p>
        </div>
        <div className="page-header-actions">
          <button
  className="btn btn-secondary"
  onClick={() => setShowFilters(s => !s)}
>
  <Filter size={15}/>
  Filtres

  {hasFilters && (
    <span
      style={{
        marginLeft:4,
        background:'#FF4100',
        color:'white',
        borderRadius:'50%',
        width:16,
        height:16,
        display:'inline-flex',
        alignItems:'center',
        justifyContent:'center',
        fontSize:'0.6rem'
      }}
    >
      {Object.values(filters).filter(Boolean).length}
    </span>
  )}
</button>
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/></button>
          {isAdmin && (
            <button className="btn btn-secondary" onClick={handleSync}><RefreshCw size={13}/> Sync CRM</button>
          )}
        </div>
      </div>

      {/* ── Stats KPI ── */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px,1fr))', gap:12, marginBottom:16 }}>
          <div className="card" style={{ padding:'12px 16px', textAlign:'center' }}>
            <div style={{ fontSize:'1.5rem', fontWeight:700, color:'#FF4100' }}>{stats.total}</div>
            <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Total</div>
          </div>
          {['Nouveau','En cour de traitement','Gagné','Perdu'].map(e => {
            const cnt = (stats.by_etat || {})[e];
            if (!cnt) return null;
            const cfg = ETAT_CONFIG[e];
            return (
              <div key={e} className="card" style={{ padding:'12px 16px', textAlign:'center', cursor:'pointer' }}
                onClick={() =>
  setFilters(f => ({
    ...f,
    etat: f.etat === e ? '' : e
  }))
}>
                <div style={{ fontSize:'1.4rem', fontWeight:700, color: cfg?.color }}>{cnt}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cfg?.label || e}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Filtres ── */}
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
  <div style={{ position:'relative', flex:1 }}>
    <Search
      size={14}
      style={{
        position:'absolute',
        left:10,
        top:'50%',
        transform:'translateY(-50%)',
        color:'var(--text-muted)'
      }}
    />

    <input
      style={{
        paddingLeft:34,
        height:38,
        border:'1px solid var(--border)',
        borderRadius:8,
        width:'100%',
        background:'var(--bg-elevated)',
        color:'var(--text-primary)'
      }}
      placeholder="Titre, référence, société..."
      value={search}
      onChange={e => setSearch(e.target.value)}
    />
  </div>

  {(search || hasFilters) && (
    <button
      className="btn btn-ghost btn-sm"
      onClick={() => {
        setSearch('');

        setFilters({
          etat:'',
          source:'',
          responsable:''
        });
      }}
    >
      <X size={14}/>
      Réinitialiser
    </button>
  )}
</div>
{showFilters && (
  <div
    className="card"
    style={{
      marginBottom:16,
      padding:'14px 16px'
    }}
  >
    <div
      style={{
        display:'grid',
        gridTemplateColumns:'repeat(3,1fr)',
        gap:10
      }}
    >

      <div>
        <label style={lbl}>Etat</label>

        <select
          className="filter-select"
          style={{ width:'100%' }}
          value={filters.etat}
          onChange={e =>
            setFilters(f => ({
              ...f,
              etat:e.target.value
            }))
          }
        >
          <option value="">Tous</option>

          {ALL_ETATS.map(e => (
            <option key={e} value={e}>
              {ETAT_CONFIG[e]?.label || e}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label style={lbl}>Source</label>

        <select
          className="filter-select"
          style={{ width:'100%' }}
          value={filters.source}
          onChange={e =>
            setFilters(f => ({
              ...f,
              source:e.target.value
            }))
          }
        >
          <option value="">Toutes</option>
          <option value="CSV">CRM</option>
          <option value="AO">AO traité</option>
        </select>
      </div>

      <div>
        <label style={lbl}>Responsable</label>

        <input
          style={inp}
          placeholder="Responsable..."
          value={filters.responsable}
          onChange={e =>
            setFilters(f => ({
              ...f,
              responsable:e.target.value
            }))
          }
        />
      </div>

    </div>
  </div>
)}

      {/* ── Tableau ── */}
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'48px', gap:10, color:'var(--text-muted)' }}>
          <div className="spinner"/> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'48px' }}>
          <Layers size={36} style={{ opacity:0.3, marginBottom:12 }}/>
          <div style={{ fontWeight:600, marginBottom:6 }}>Aucun besoin trouvé</div>
          <div style={{ fontSize:'0.875rem', color:'var(--text-muted)' }}>{search || hasFilters ? 'Modifiez les filtres' : 'Les besoins apparaîtront ici après synchronisation'}</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Référence</th>
                <th>Titre</th>
                <th>Société</th>
                <th>État</th>
                <th>Source</th>
                <th>Lieu</th>
                <th>Démarrage</th>
                <th>Responsable</th>
                <th style={{ width:36 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(b => (
                <tr
                  key={b.id}
                  onClick={() => navigate(`/besoins/${b.id}`)}
                  style={{ cursor:'pointer' }}
                  title="Cliquer pour voir les détails"
                >
                  <td>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:'0.78rem', color:'var(--text-muted)' }}>
                      {b.reference || '—'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontWeight:600, fontSize:'0.875rem' }}>
                      {b.titre || '—'}
                    </span>
                    {b.type_besoin && (
                      <span style={{ display:'block', fontSize:'0.72rem', color:'var(--text-muted)', marginTop:1 }}>{b.type_besoin}</span>
                    )}
                  </td>
                  <td>
                    {b.societe_nom ? (
                      <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.825rem' }}>
                        <Building2 size={12} style={{ flexShrink:0, color:'var(--text-muted)' }}/>{b.societe_nom}
                      </span>
                    ) : '—'}
                  </td>
                  <td><EtatBadge etat={b.etat}/></td>
                  <td><SourceBadge source={b.source}/></td>
                  <td>
                    {b.lieu ? (
                      <span style={{ fontSize:'0.8rem', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:3 }}>
                        <MapPin size={11}/>{b.lieu}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                      {b.date_demarrage || '—'}
                    </span>
                  </td>
                  <td>
                    {b.responsable_manager ? (
                      <span style={{ fontSize:'0.8rem', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:3 }}>
                        <User size={11}/>{b.responsable_manager}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <ChevronRight size={15} color="var(--text-muted)"/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
const lbl = {
  display:'block',
  fontSize:'0.75rem',
  fontWeight:600,
  color:'var(--text-muted)',
  marginBottom:4,
  textTransform:'uppercase',
  letterSpacing:'0.04em'
};

const inp = {
  width:'100%',
  padding:'8px 10px',
  border:'1.5px solid var(--border)',
  borderRadius:7,
  fontSize:'0.875rem',
  boxSizing:'border-box',
  fontFamily:'inherit',
  outline:'none',
  background:'var(--bg-elevated)',
  color:'var(--text-primary)'
};